import React, { ReactNode } from 'react';
import { FallbackProps, ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { Space, Title, Text, Button } from '@g2u/ui-kit';
import { datadogRum } from '@datadog/browser-rum';
import { Card, Image, Spin } from 'antd';

import { BookingStore } from 'store';
import Logo from 'assets/images/geeks2u-logo-error.svg';

type ErrorBoundaryProps = {
  children: ReactNode;
};

const loadingStyles: React.CSSProperties = {
  position: 'absolute',
  zIndex: -1,
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
};

const bannerStyles: React.CSSProperties = {
  width: 480,
  left: '50%',
  top: '10%',
  transform: 'translate(-50%, 0)',
  position: 'absolute',
};

const ErrorFallback: React.FC<FallbackProps> = ({ error }) => {
  const { resetBookingProgress } = BookingStore;

  const handleAction = () => {
    resetBookingProgress();
    window.location.href = '/dashboard';
  };

  return (
    <Card variant="outlined" style={bannerStyles}>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
        <Space.$ size={Space.Size.Middle}>
          <Image src={Logo} alt="Geeks2U logo error" preview={false} width={40} />
          <Title.$ strong level={2} style={{ fontFamily: 'OfficeworksWeb-Medium' }}>
            Application Error
          </Title.$>
        </Space.$>
        <Space.$ direction={Space.Direction.Vertical}>
          <Text.$>We apologize for the inconvenience.</Text.$>
          <Text.$>Our team has been notified and is investigating the issue.</Text.$>
        </Space.$>
        <Button.$ type={Button.Type.Primary} onClick={handleAction} dataTestId="resolve-error-btn">
          Go to Homepage
        </Button.$>
        <details style={{ whiteSpace: 'pre-wrap', cursor: 'pointer' }} title="Error Details">
          <Text.$ type="danger">{error.message}</Text.$>
        </details>
      </Space.$>
    </Card>
  );
};

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        const currentUrl = window.location.href;
        const rootStore = sessionStorage.getItem('root-store');
        const bookingStore = sessionStorage.getItem('booking-store');

        datadogRum.addError(error, {
          errorInfo: errorInfo.componentStack,
          errorSource: 'ErrorBoundary',
          extraInfo: {
            currentUrl,
            rootStore,
            bookingStore,
          },
          // @ts-expect-error connection only defined in some browsers, hence type error
          connection: navigator?.connection,
          online: navigator?.onLine,
        });
      }}
    >
      {children}
      <Space.$ justify={Space.Justify.Center} align={Space.Align.Center} style={loadingStyles}>
        <Spin size="large" />
      </Space.$>
    </ReactErrorBoundary>
  );
};
